// Utilitários centralizados para o sistema de acesso e assinatura das consultoras

// ─────────────────────────────────────────────────────────────────────────────
// ASSINATURA ÚNICA DO SISTEMA — altere preço e link somente aqui.
// Tudo o que a tela de assinatura mostra vem deste bloco.
// ─────────────────────────────────────────────────────────────────────────────
export const ASSINATURA = {
  titulo: 'Assinatura anual do Sistema Descubra seus Óleos',
  preco: 'R$147',
  precoTexto: 'R$147 por ano',
  parcelado: '12x de R$15,20 no cartão',
  botao: 'Assinar por R$147 ao ano',
  link: 'https://pay.hotmart.com/V105595135W?off=dcwfc5is',
}

// Links da Hotmart (legado — mantidos para registros históricos)
export const LINK_HOTMART = 'https://pay.hotmart.com/V105595135W?off=ucn22vfo'
export const LINK_HOTMART_MENSAL = 'https://pay.hotmart.com/V105595135W?off=ucn22vfo'
export const LINK_HOTMART_ANUAL = 'https://pay.hotmart.com/V105595135W?off=33zo7s3t'

// Tipo de plano de assinatura: essencial (básico) ou completo (Plano Diamante: Empresas/NR-1)
export type PlanoAssinatura = 'essencial' | 'completo'

// Links da Hotmart por plano e periodicidade
export const LINKS_PLANOS: Record<PlanoAssinatura, { mensal: string; anual: string }> = {
  essencial: {
    mensal: 'https://pay.hotmart.com/V105595135W?off=eq3sfm5n',
    anual: 'https://pay.hotmart.com/V105595135W?off=dcwfc5is',
  },
  completo: {
    mensal: 'https://pay.hotmart.com/V105595135W?off=ucn22vfo',
    anual: 'https://pay.hotmart.com/V105595135W?off=33zo7s3t',
  },
}

// Controle único da promoção de lançamento nos cards de planos.
// false = esconde o selo "Promoção de lançamento" e o preço riscado.
export const PROMOCAO_LANCAMENTO = true

// Mapa de offer code da Hotmart → { plano, periodicidade }
export const OFFER_CODE_PARA_PLANO: Record<string, { plano: PlanoAssinatura; periodicidade: 'mensal' | 'anual' }> = {
  'eq3sfm5n': { plano: 'essencial', periodicidade: 'mensal' },
  'dcwfc5is': { plano: 'essencial', periodicidade: 'anual' },
  'ucn22vfo': { plano: 'completo', periodicidade: 'mensal' },
  '33zo7s3t': { plano: 'completo', periodicidade: 'anual' },
}

// Verifica se a consultora tem acesso à aba Empresas.
// A assinatura é única: qualquer registro de assinatura (essencial ou completo)
// libera a aba. Contas sem assinatura gravada seguem sem acesso, como antes.
// As regras de status e vencimento continuam sendo aplicadas no login/sessão.
export function temAcessoEmpresas(
  consultora: { planoAssinatura?: PlanoAssinatura } | null | undefined,
): boolean {
  const registro = consultora?.planoAssinatura
  return registro === 'essencial' || registro === 'completo'
}

// Mantido por compatibilidade: hoje equivale ao acesso à aba Empresas.
export function temPlanoCompleto(consultora: { planoAssinatura?: PlanoAssinatura } | null | undefined): boolean {
  return temAcessoEmpresas(consultora)
}

export type TipoAcesso = 'gratuito' | 'mensal' | 'anual'
export type StatusAssinatura = 'ativo' | 'gratuito' | 'expirado' | 'inativo'

export interface ConsultoraAssinatura {
  _id?: string
  nome: string
  usuario: string
  email?: string
  whatsapp?: string
  senha: string
  ativo: boolean
  tipoAcesso?: TipoAcesso
  statusAssinatura?: StatusAssinatura
  planoAssinatura?: PlanoAssinatura
  dataExpiracao?: string // ISO 8601 ou '' quando gratuito
  distribuidora?: string // slug da distribuidora vinculada
  migradoLegado?: boolean
  enviouAvisoVencimento3Dias?: boolean
  createdAt: string
  updatedAt: string
}

// Adiciona dias a uma data (retorna ISO UTC)
export function adicionarDias(base: Date, dias: number): string {
  const d = new Date(base.getTime())
  d.setUTCDate(d.getUTCDate() + dias)
  return d.toISOString()
}

// Calcula a data de expiração com base no tipo de acesso
export function calcularDataExpiracao(tipo: TipoAcesso): string {
  const agora = new Date()
  switch (tipo) {
    case 'mensal': return adicionarDias(agora, 30)
    case 'anual': return adicionarDias(agora, 365)
    case 'gratuito': return ''
  }
}

// Calcula dias restantes até a expiração (pode ser negativo quando já expirou)
export function diasRestantes(dataExpiracao?: string): number | null {
  if (!dataExpiracao) return null
  const exp = new Date(dataExpiracao).getTime()
  const agora = Date.now()
  const diffMs = exp - agora
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

// Verifica se o acesso está atualmente liberado
export function acessoLiberado(consultora: Partial<ConsultoraAssinatura>): boolean {
  if (!consultora.ativo) return false
  const status = consultora.statusAssinatura
  const tipo = consultora.tipoAcesso

  // Gratuito (legado ou liberado manualmente) nunca expira
  if (status === 'gratuito' || tipo === 'gratuito') return true
  if (consultora.migradoLegado) return true

  if (status === 'expirado' || status === 'inativo') return false

  // mensal / anual → checar data de expiração
  if (consultora.dataExpiracao) {
    const exp = new Date(consultora.dataExpiracao).getTime()
    if (Date.now() > exp) return false
  }

  return true
}

// Formata data ISO em dd/mm/yyyy
export function formatarData(iso?: string): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

// Rótulo amigável do status
export function rotuloStatus(status?: StatusAssinatura): string {
  const map: Record<string, string> = {
    ativo: 'Ativo',
    gratuito: 'Gratuito',
    expirado: 'Expirado',
    inativo: 'Inativo',
  }
  return map[status || ''] || '—'
}

// Cores do badge de status de assinatura
export function coresStatusAssinatura(status?: StatusAssinatura): { bg: string; text: string } {
  switch (status) {
    case 'ativo': return { bg: '#D8F3DC', text: '#2D6A4F' }
    case 'gratuito': return { bg: '#E6F1FB', text: '#185FA5' }
    case 'expirado': return { bg: '#FCEBEB', text: '#A32D2D' }
    case 'inativo': return { bg: '#F3F4F6', text: '#4B5563' }
    default: return { bg: '#F3F4F6', text: '#6B7280' }
  }
}
